import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import * as styles from './ExternalTopbar.module.scss';
import PostModeToggle from './PostModeToggle';
import authTokenUtil from '../account/authTokenUtil';
import getRedirectQueryString from '../account/getRedirectQueryString';
import { useLogout } from '../account/localLogout';
import useLegacyIsLoggedIn from '../account/useIsLoggedIn';
import { CONTEXT_AUTH_TOKEN_KEY } from '../app/apollo/getTransportLink';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector from '../app/redux/useAppSelector';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Caret from '../components/Caret';
import PopOver from '../components/deprecated/PopOver';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import Menu, {
  MenuItem,
  MenuItemRadio,
  MenuSeparator,
} from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import { gql } from '../graphql/generated/gql';
import { PostLinkAccess, UserType } from '../graphql/generated/graphql';
import {
  HelpDocumentationMenuItem,
  ProductUpdatesMenuItem,
} from '../help/HelpMenu';
import docky from '../images/docky.svg';
import TeamIcon from '../organization/TeamIcon';
import Presence from '../post/Presence';
import useIsPublicDocumentationView from '../public/useIsPublicDocumentationView';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';
import Link from '../utils/router/Link';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';
import safeParseUrl from '../utils/url/safeParseUrl';
import topicPath from '../utils/url/topicPath';

const POST_QUERY = gql(/** @complexity 1 */ `
  query ExternalTopbarPost($id: ID!) {
    publicPost(id: $id) {
      id
      linkAccess
    }
  }
`);

const USER_QUERY = gql(/** @complexity 3 */ `
  query ExternalTopbarUser {
    currentSession {
      user {
        id
        ...Avatar_user
      }
    }
  }
`);

interface BaseExternalTopbarProps {
  children: React.ReactNode;
}
const BaseExternalTopbar = ({ children }: BaseExternalTopbarProps) => {
  return (
    <PageLayout.Topbar className={styles.externalTopbar}>
      {children}
    </PageLayout.Topbar>
  );
};

const TeamIconLink = () => {
  const publicLogo = window.__slab.PUBLIC_ORG.settings.public?.logo.original;

  return (
    <Link to="/">
      {publicLogo ? (
        <div className={styles.publicLogo}>
          <img src={publicLogo} height="24" alt="Logo" />
        </div>
      ) : (
        <TeamIcon />
      )}
    </Link>
  );
};

const TopbarMenu = () => {
  const [topicId] = useSlug('topicSlug');
  const [postId] = useSlug('postSlug');
  const redirectPath = useAppSelector(
    (state) => state.ui.externalTopbarLoginRedirectPath,
  );
  const isLoggedIn = useLegacyIsLoggedIn();
  const logout = useLogout({ preventRedirect: true });

  const { data: avatarData, loading: isLoadingAvatar } = useQuery(USER_QUERY, {
    context: { [CONTEXT_AUTH_TOKEN_KEY]: authTokenUtil.get() },
    skip: !isLoggedIn,
  });

  const [isPopOverOpen, setIsPopOverOpen] = useState(false);

  return (
    <PopOver
      display="inline-block"
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      popOver={
        <Menu>
          {isLoggedIn ? (
            <>
              {!!postId && (
                <MenuItem to={postPath({ id: postId, title: '' })} wrap>
                  View in Slab
                </MenuItem>
              )}
              {!!topicId && (
                <MenuItem to={topicPath({ id: topicId, name: '' })} wrap>
                  View in Slab
                </MenuItem>
              )}
            </>
          ) : (
            <MenuItem to={`/login${getRedirectQueryString(redirectPath)}`} wrap>
              Log in to Slab
            </MenuItem>
          )}
          <ProductUpdatesMenuItem wrap noIcon />
          <HelpDocumentationMenuItem wrap noIcon />
          <MenuSeparator />
          {isLoggedIn ? (
            <MenuItem onClick={logout} wrap to="/login">
              Log out
            </MenuItem>
          ) : (
            <MenuItem to="https://slab.com" wrap newTab>
              <div className={styles.trySlab}>
                <div className={styles.trySlabLogo}>
                  <ExternalBrandLogo brand="slab" product="slab" />
                </div>
                <div>
                  <div className={styles.trySlabTitle}>Try Slab</div>
                  <div className={styles.trySlabDescription}>
                    Free up to 10 users
                  </div>
                </div>
              </div>
            </MenuItem>
          )}
        </Menu>
      }
      placement="bottom-start"
      popOverPortal={document.body}
      responsivePlacement="bottom"
      trigger="click"
      open={isPopOverOpen}
      onOpenChange={setIsPopOverOpen}
    >
      <div
        className={classNames(styles.topbarMenu, {
          [styles.isPopOverOpen]: isPopOverOpen,
        })}
      >
        <div
          className={classNames(styles.logo, {
            [styles.loading]: isLoadingAvatar,
          })}
        >
          {avatarData?.currentSession?.user ? (
            <Avatar size={24} user={avatarData.currentSession.user} />
          ) : (
            <img src={docky} width={24} alt="Slab" />
          )}
        </div>
        <div className={styles.caret}>
          <Caret />
        </div>
      </div>
    </PopOver>
  );
};

const PostToggleButton = () => {
  const [postId] = useSlug('postSlug');
  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isEditorReady = useAppSelector((state) => state.editor.version >= 0);
  const { data: postData } = useQuery(POST_QUERY, {
    variables: { id: postId || '' },
    skip: !postId,
  });
  const isMobile = useIsSize(Sizes.SM);

  if (
    postData?.publicPost.linkAccess !== PostLinkAccess.PublicEdit ||
    !isEditorReady
  ) {
    return null;
  }

  return (
    <>
      {!readOnly && currentUser.type === UserType.Visitor && (
        <div className={styles.editingNote}>
          You are editing as {currentUser.name}
        </div>
      )}
      {!readOnly && !isMobile && <Presence />}
      <PostModeToggle />
      <div className={styles.modeToggleSeparator} />
    </>
  );
};

const { host } = window.location;

const TopbarNavigation = () => {
  const links = window.__slab.PUBLIC_ORG.settings.public?.links ?? [];
  const isMobile = useIsSize(Sizes.SM);

  const [isOpen, setIsOpen] = useState(false);

  if (links.length === 0) return null;

  const linksWithExternalState = links.map((link) => ({
    ...link,
    isExternal: safeParseUrl(link.url)?.host !== host,
  }));

  if (isMobile) {
    const selectedIndex = Math.max(
      linksWithExternalState.findIndex((link) => !link.isExternal),
      0,
    );
    return (
      <OverflowMenu
        onOpenChange={setIsOpen}
        open={isOpen}
        button={
          <Button
            className={styles.mobileNavigationButton}
            testId="accessLevelSelectButton"
          >
            {linksWithExternalState[selectedIndex]?.text}
            <div className={styles.caret}>
              <Caret direction="down" />
            </div>
          </Button>
        }
        modifiers={[{ name: 'offset', options: { offset: [6, 0] } }]}
      >
        {links.map((link, index) => {
          return (
            <MenuItemRadio key={link.text} checked={index === selectedIndex}>
              {link.text}
            </MenuItemRadio>
          );
        })}
      </OverflowMenu>
    );
  }

  return (
    <div className={styles.navigation}>
      {linksWithExternalState.map((link, index) => (
        <Link
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          to={link.url}
          className={classNames(styles.link, {
            [styles.isActive]: !link.isExternal,
          })}
          newTab={link.isExternal}
        >
          {link.text}
        </Link>
      ))}
    </div>
  );
};

const ExternalTopbar = () => {
  const isPublicDocumentationView = useIsPublicDocumentationView();
  return (
    <BaseExternalTopbar>
      <TeamIconLink />
      {isPublicDocumentationView && <TopbarNavigation />}
      <div className={styles.actions}>
        {!isPublicDocumentationView && <PostToggleButton />}
        <TopbarMenu />
      </div>
    </BaseExternalTopbar>
  );
};

export default ExternalTopbar;
